import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, MapPin, Clock, X } from 'lucide-react';
import { useNativeCamera } from '@/hooks/useNativeCamera';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface PhotoCaptureStepProps {
  photos: any[];
  onPhotosChange: (photos: any[]) => void;
}

export function PhotoCaptureStep({ photos, onPhotosChange }: PhotoCaptureStepProps) {
  const { capturePhoto, isCapturing } = useNativeCamera();
  const [capturing, setCapturing] = useState(false);

  const handleCapture = async () => {
    setCapturing(true);
    const photo = await capturePhoto();
    
    if (photo) {
      onPhotosChange([...photos, photo]);
    }
    
    setCapturing(false);
  };

  const handleRemove = (index: number) => {
    onPhotosChange(photos.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 text-center">
        <Camera className="w-12 h-12 mx-auto mb-3 text-primary" />
        <h3 className="font-semibold mb-2">Capture Photos</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Photos are automatically timestamped and geotagged
        </p>
        <Button
          onClick={handleCapture}
          disabled={capturing || isCapturing}
          size="lg"
          className="w-full"
        >
          <Camera className="w-5 h-5 mr-2" />
          {capturing || isCapturing ? 'Opening Camera...' : 'Take Photo'}
        </Button>
      </Card>

      {photos.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium">
            Captured Photos ({photos.length})
          </h4>
          {photos.map((photo, index) => (
            <Card key={index} className="p-3">
              <div className="flex gap-3">
                <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={photo.dataUrl}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1 min-w-0">
                      <p className="font-medium text-sm">Photo {index + 1}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {format(new Date(photo.timestamp), 'h:mm a')}
                      </div>
                      {photo.latitude && photo.longitude && (
                        <Badge variant="outline" className="text-xs">
                          <MapPin className="w-3 h-3 mr-1" />
                          GPS Tagged
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(index)}
                      className="flex-shrink-0 h-8 w-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {photos.length === 0 && (
        <Card className="p-8 text-center border-dashed">
          <p className="text-sm text-muted-foreground">
            No photos captured yet. Take at least one photo to continue.
          </p>
        </Card>
      )}
    </div>
  );
}
